/**
 * AdminIntegrationIcons — Admin page to preview all integration icons
 * Shows icons organized by category with metadata
 */
import { IntegrationIcon, INTEGRATIONS, CATEGORY_LABELS, type IntegrationDef } from '../components/IntegrationIcon';

import { T } from '../styles/tokens';
const SIZES = [16, 20, 24, 32, 48];

export function AdminIntegrationIcons() {
  // Group by category
  const byCategory: Record<string, Array<{ key: string; def: IntegrationDef }>> = {};
  for (const [key, def] of Object.entries(INTEGRATIONS)) {
    if (!byCategory[def.category]) byCategory[def.category] = [];
    byCategory[def.category].push({ key, def });
  }

  const categoryOrder = ['cloud', 'dev', 'messaging', 'email', 'payment', 'monitoring', 'marketing', 'ai', 'other'];

  return (
    <div style={{ padding: '32px 24px', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: 0 }}>
          Ícones de Integrações
        </h1>
        <p style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>
          {Object.keys(INTEGRATIONS).length} integrações · SVG inline · Sem dependência externa
        </p>
      </div>

      {/* Size preview row */}
      <div style={{
        padding: 16, marginBottom: 24, borderRadius: 10,
        background: T.bgSubtle, border: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
          Preview de Tamanhos
        </div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'end' }}>
          {SIZES.map(s => (
            <div key={s} style={{ textAlign: 'center' }}>
              <IntegrationIcon name="cloudflare" size={s} />
              <div style={{ fontSize: 9, color: T.textDisabled, marginTop: 4 }}>{s}px</div>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      {categoryOrder.map(cat => {
        const items = byCategory[cat];
        if (!items?.length) return null;
        return (
          <div key={cat} style={{ marginBottom: 28 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: T.textSecondary,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: 12, paddingBottom: 8,
              borderBottom: `1px solid ${T.border}`,
            }}>
              {CATEGORY_LABELS[cat] || cat} ({items.length})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.map(({ key, def }) => (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px',
                  borderRadius: 8, background: T.bgSubtle,
                  border: `1px solid ${T.borderSubtle}`,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCardHover}
                onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgSubtle}
                >
                  {/* Icon at multiple sizes */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 140 }}>
                    <IntegrationIcon name={key} size={16} />
                    <IntegrationIcon name={key} size={24} />
                    <IntegrationIcon name={key} size={32} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{def.label}</span>
                      <span style={{ fontSize: 10, color: T.textDisabled, fontFamily: 'monospace' }}>{key}</span>
                    </div>
                    {def.website && (
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{def.website}</div>
                    )}
                  </div>

                  {/* Color swatch */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 4,
                      background: def.color,
                      border: `1px solid ${T.borderHover}`,
                    }} />
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.textMuted }}>
                      {def.color}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div style={{
        marginTop: 32, padding: 16, borderRadius: 10,
        background: T.bgSubtle, border: `1px solid ${T.border}`,
        fontSize: 11, color: T.textDisabled, lineHeight: 1.8,
      }}>
        <strong style={{ color: T.textMuted }}>Uso no código:</strong><br />
        {`import { IntegrationIcon } from '../components/IntegrationIcon';`}<br />
        {`<IntegrationIcon name="cloudflare" size={24} />`}<br />
        {`<IntegrationIcon name="github" size={18} showLabel />`}<br /><br />
        <strong style={{ color: T.textMuted }}>Fonte:</strong> Simple Icons (simpleicons.org) — SVG paths oficiais das marcas
      </div>
    </div>
  );
}
